import { Box, Card, Divider, InputAdornment, TextField, Typography } from '@mui/material';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAtomValue, useSetAtom } from 'jotai';
import CommentOutlinedIcon from '@mui/icons-material/CommentOutlined';
import { FormatLogisticalPackaging } from '@kheops/utils';
import { generatePath, Link } from 'react-router';
import { BasketOrder } from '../hooks/useBasketOrders';
import DeliveryDatePicker from '../common/components/DeliveryDatePicker';
import { contractByReferenceIdAtom } from '../state';
import { RoutePaths } from '../routes/AppRoutes';
import { linkStyle } from '../common/utils/style.utils';
import { BasketOrderPrice } from './BasketOrderPrice';
import { currentOrdersCommentsAtom, currentOrdersDeliveryDatesAtom } from './state/state';
import BasketItemSummary from './BasketItemSummary';
import SizedImage from '../common/components/SizedImage';
import CompanyLogo from '../common/components/CompanyLogo';

interface BasketOrderSummaryProps {
  basketOrder: BasketOrder;
  contractReferenceId: string;
}

export default function BasketOrderSummary({ basketOrder, contractReferenceId }: BasketOrderSummaryProps): React.JSX.Element {
  const { t } = useTranslation(['common', 'order']);

  const { photoUrl, name, items, brand, companyId } = basketOrder;
  const companyLink = generatePath(RoutePaths.CONTRACT_COMPANY, { companyId });

  const [value, setValue] = useState<Date | null>(null);
  const contracAtom = useMemo(() => {
    return contractByReferenceIdAtom(contractReferenceId);
  }, [contractReferenceId]);
  const contract = useAtomValue(contracAtom);
  const updateCurrentOrdersDeliveryDates = useSetAtom(currentOrdersDeliveryDatesAtom);
  const updateCurrentOrdersComments = useSetAtom(currentOrdersCommentsAtom);

  const handleDeliveryDateChange = (newValue: Date | null): void => {
    setValue(newValue);
    updateCurrentOrdersDeliveryDates({ contractId: contractReferenceId, deliveryDate: newValue });
  };

  const handleCommentChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const input = event.target.value;
    updateCurrentOrdersComments({ contractId: contractReferenceId, comment: input.length ? input : null });
  };

  useEffect((): void => {
    updateCurrentOrdersDeliveryDates();
  }, []);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
        <Box
          component={Link}
          to={companyLink}
          aria-label={name}>
          <SizedImage
            src={photoUrl}
            alt={t('common:company')}
            width={72}
            height={72}
            borderRadius={3}
            overlay={brand && (
              <CompanyLogo
                variant="square"
                size="small"
                brand={brand}
                sx={{
                  position: 'absolute',
                  bottom: 4,
                  width: 32,
                  right: 4,
                }}
              />
            )}
          />
        </Box>
        <Box>
          <Box
            component={Link}
            to={companyLink}
            sx={{ ...linkStyle, py: 0.5 }}
          >
            <Typography variant="bodyLarge">
              {name} ({items.length})
            </Typography>
          </Box>
          <BasketOrderPrice basketOrder={basketOrder} />
        </Box>
      </Box>
      <Box sx={{ mt: 1.5 }}>
        {
          basketOrder.items
            .sort(({ packaging: pa }, { packaging: pb }) => {
              const packagingNameA = `${pa.product!.name}_${FormatLogisticalPackaging(pa, 'fr', pa.base_unit!)}`;
              const packagingNameB = `${pb.product!.name}_${FormatLogisticalPackaging(pb, 'fr', pb.base_unit!)}`;

              return packagingNameA.localeCompare(packagingNameB);
            })
            .map((item) => (
              <Card
                key={item.packaging.id}
                sx={{
                  backgroundColor: 'surfaceContainerLow.main',
                  px: 1.5,
                  pt: 2,
                  pb: 0,
                  mb: 1.5,
                  borderRadius: 4,
                  '& .MuiChip-tag': {
                    backgroundColor: 'surfaceContainerHigh.main',
                  },
                }}
              >
                <BasketItemSummary item={item} editable priceVariant="titleMedium" />
              </Card>
            ))
        }
      </Box>
      <Card
        sx={{
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: 'transparent',
          border: '1px solid',
          borderColor: 'divider',
          gap: 3,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="titleSmall">{t('order:suggest_delivery_date')}</Typography>
          <DeliveryDatePicker companyId={contract!.buyingCompanyId} value={value} onChange={handleDeliveryDateChange} />
        </Box>
        <Divider />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="titleSmall">{t('comments:comments', { count: 1 })}</Typography>
          <TextField
            variant="outlined"
            multiline
            placeholder={t('comments:add_a_comment')}
            onChange={handleCommentChange}
            rows={3}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start" sx={{ alignSelf: 'baseline', alignItems: 'baseline' }}>
                    <CommentOutlinedIcon sx={{ width: 20, height: 20 }} color="secondary" />
                  </InputAdornment>
                ),
              },
            }}
          />
        </Box>
      </Card>
    </Box>
  );
}
